import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TeamProvider, useTeam } from "@/contexts/TeamContext";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { ApprovalModeProvider } from "@/contexts/ApprovalModeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { OnboardingSheet } from "@/components/OnboardingSheet";
import { lazy, Suspense, Component, ReactNode, useEffect, useState, useRef } from "react";
import { MessageCircleQuestion, X, ExternalLink } from "lucide-react";
import { analytics } from "@/lib/analytics";

// ---------------------------------------------------------------------------
// Chunk-load error recovery
//
// Problem: Every Vite production build content-hashes JS chunks
// (e.g. Index-CjKT42yE.js).  On a new deployment ALL hashes change.
// If the browser/CDN has a cached copy of the OLD index.html it still
// references the OLD chunk filenames.  When React Router tries to lazy-load
// a route it fetches the old URL → 404 → "TypeError: error loading
// dynamically imported module" → white screen.
//
// Fix: wrap every lazy() call so that on the first import failure we
// reload the page once (picking up the fresh index.html + new chunk URLs).
// A sessionStorage flag prevents an infinite reload loop.
// ---------------------------------------------------------------------------
const CHUNK_RELOAD_KEY = "vite-chunk-reload-attempted";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends { default: React.ComponentType<any> }>(
  importFn: () => Promise<T>
): React.LazyExoticComponent<React.ComponentType<any>> {
  return lazy(() =>
    importFn().catch((err) => {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        // Keep the promise pending — the reload will navigate away before it resolves
        return new Promise<never>(() => {});
      }
      // Already reloaded and still failing → let the ErrorBoundary handle it
      throw err;
    })
  );
}

// ---------------------------------------------------------------------------
// Error boundary: last-resort UI when a chunk fails even after a reload
// ---------------------------------------------------------------------------
interface ChunkErrorBoundaryState { hasError: boolean }

class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  ChunkErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true };
  }

  handleReload = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
          <p className="text-lg font-medium">Something went wrong loading this page.</p>
          <p className="text-muted-foreground text-sm">
            A new version of the app may have been deployed. Please reload to get the latest version.
          </p>
          <button
            className="px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90"
            onClick={this.handleReload}
          >
            Reload app
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load all page components for code splitting
const Index = lazyWithRetry(() => import("./pages/Index"));
const CreateActivity = lazyWithRetry(() => import("./pages/CreateActivity"));
const CreateTemplate = lazyWithRetry(() => import("./pages/CreateTemplate"));
const ManageTemplatesAndActivities = lazyWithRetry(() => import("./pages/ManageTemplatesAndActivities"));
const ActionsList = lazyWithRetry(() => import("./pages/ActionsList"));
const RiskIssueLogs = lazyWithRetry(() => import("./pages/RiskIssueLogs"));
const AddRiskIssue = lazyWithRetry(() => import("./pages/AddRiskIssue"));
const StrategicDashboardV2 = lazyWithRetry(() => import("./pages/StrategicDashboardV2"));
const ArcanixLabs = lazyWithRetry(() => import("./pages/ArcanixLabs"));
const UpcomingFeatures = lazyWithRetry(() => import("./pages/UpcomingFeatures"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const AccountPreferences = lazyWithRetry(() => import("./pages/AccountPreferences"));
const Subscription = lazyWithRetry(() => import("./pages/Subscription"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const SystemTemplatesAdmin = lazyWithRetry(() => import("./pages/SystemTemplatesAdmin"));
const SystemTagsAdmin = lazyWithRetry(() => import("./pages/SystemTagsAdmin"));
const OrganizationSettings = lazyWithRetry(() => import("./pages/OrganizationSettings"));
const OrganizationIntegrations = lazyWithRetry(() => import("./pages/OrganizationIntegrations"));
const TeamImports = lazyWithRetry(() => import("./pages/TeamImports"));
const ImportReview = lazyWithRetry(() => import("./pages/ImportReview"));
const WeeklyInsightsPage = lazyWithRetry(() => import("./pages/WeeklyInsightsPage"));
const EnterpriseDashboard = lazyWithRetry(() => import("./pages/EnterpriseDashboard"));
const EnterpriseOrganizations = lazyWithRetry(() => import("./pages/EnterpriseOrganizations"));
const ComplianceDashboard = lazyWithRetry(() => import("./pages/ComplianceDashboard"));
const UserOnboarding = lazyWithRetry(() => import("@/components/UserOnboarding"));
const Join = lazyWithRetry(() => import("./pages/Join"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const ApprovalWorkflowTest = lazyWithRetry(() => import("./pages/ApprovalWorkflowTest"));
const ChartDispatcherTest = lazyWithRetry(() => import("./pages/ChartDispatcherTest"));
const ChartPackageTest = lazyWithRetry(() => import("./pages/ChartPackageTest"));
const RevenueEventsTest = lazyWithRetry(() => import("./pages/RevenueEventsTest"));
const RevenueEventsDispatcherTest = lazyWithRetry(() => import("./pages/RevenueEventsDispatcherTest"));
const ChartEventsExplorer = lazyWithRetry(() => import("./pages/ChartEventsExplorer"));
const RevenueAttributionTest = lazyWithRetry(() => import("./pages/RevenueAttributionTest"));
const GamescomRevenueAttributionTest = lazyWithRetry(() => import("./pages/GamescomRevenueAttributionTest"));
const GamescomRevenueAttributionPopupTest = lazyWithRetry(() => import("./pages/GamescomRevenueAttributionPopupTest"));
const GamescomRevenueAttributionPopupPackagesTest = lazyWithRetry(() => import("./pages/GamescomRevenueAttributionPopupPackagesTest"));
const GamesopsV1 = lazyWithRetry(() => import("./pages/GamesopsV1"));
const GamesopsV2 = lazyWithRetry(() => import("./pages/GamesopsV2"));
const GamesopsV3 = lazyWithRetry(() => import("./pages/GamesopsV3"));
const TestIndex = lazyWithRetry(() => import("./pages/TestIndex"));
const ActivityTimeline = lazyWithRetry(() => import("./pages/ActivityTimeline"));
const ApiDocs = lazyWithRetry(() => import("./pages/ApiDocs"));
const GettingStarted = lazyWithRetry(() => import("./pages/GettingStarted"));

// Route preloading - preload components on hover
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const preloadRoute = (importFn: () => Promise<unknown>) => {
  importFn();
};

export const preloadRoutes = {
  index: () => preloadRoute(() => import("./pages/Index")),
  profile: () => preloadRoute(() => import("./pages/AccountPreferences")),
  admin: () => preloadRoute(() => import("./pages/AdminDashboard")),
  createActivity: () => preloadRoute(() => import("./pages/CreateActivity")),
  strategic: () => preloadRoute(() => import("./pages/StrategicDashboardV2")),
  upcomingFeatures: () => preloadRoute(() => import("./pages/UpcomingFeatures")),
  actions: () => preloadRoute(() => import("./pages/ActionsList")),
  riskLogs: () => preloadRoute(() => import("./pages/RiskIssueLogs")),
};

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const SuspenseRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

// ---------------------------------------------------------------------------
// Page-view tracker — fires analytics.pageView on every route change
// Must be rendered inside <BrowserRouter>
// ---------------------------------------------------------------------------
const PageViewTracker = () => {
  const location = useLocation();
  const prevLocationRef = useRef<string>('');
  
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Always track, including initial mount
    analytics.pageView(currentPath);
    prevLocationRef.current = currentPath;
  }, [location]);
  
  return null;
};

const JSM_SUPPORT_URL = 'https://arcanix-team.atlassian.net/helpcenter/ACS/contact-us/25e10061-3343-4766-ba25-dbb08378043c';
const SUPPORT_HIDDEN_KEY = 'arcanix_support_btn_hidden';

/** Floating support button — visible on all authenticated pages. */
const SupportButton = () => {
  const { profile, user } = useAuth();
  const { activeTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(SUPPORT_HIDDEN_KEY) === '1');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (hidden) return null;

  const openSupportForm = () => {
    const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'Unknown';
    const email = profile?.email || user?.email || '';
    const team = activeTeam?.name || 'N/A';
    const page = window.location.href;

    const description = [
      `Reporter: ${name}`,
      `Email: ${email}`,
      `Team: ${team}`,
      `Page: ${page}`,
      '',
      'Please describe the issue below:',
      '',
    ].join('\n');

    const url = new URL(JSM_SUPPORT_URL);
    url.searchParams.set('summary', 'Support request from Arcanix');
    url.searchParams.set('description', description);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const hideForSession = () => {
    sessionStorage.setItem(SUPPORT_HIDDEN_KEY, '1');
    setHidden(true);
    setOpen(false);
  };

  return (
    <div ref={popoverRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {/* Popover card */}
      {open && (
        <div className="w-64 rounded-lg border border-border bg-popover text-popover-foreground shadow-xl p-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold leading-tight">Need help?</p>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Report a bug or request support. Your name, email, and current page will be pre-filled in the form.
          </p>
          <button
            onClick={openSupportForm}
            className="flex items-center justify-center gap-2 w-full rounded-md bg-primary text-primary-foreground text-xs font-medium py-2 hover:opacity-90 transition-opacity"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open support form
          </button>
          <button
            onClick={hideForSession}
            className="text-xs text-muted-foreground hover:text-foreground text-center underline-offset-2 hover:underline transition-colors"
          >
            Hide for this session
          </button>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Get support"
        aria-expanded={open}
        className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 active:scale-95 transition-all"
      >
        <MessageCircleQuestion className="w-5 h-5" />
      </button>
    </div>
  );
};

const App = () => (
  <ChunkErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ApprovalModeProvider>
            <TeamProvider>
              <NotificationProvider>
                <TimezoneProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
              <PageViewTracker />
              <Routes>
                {/* Public routes */}
                <Route path="/auth" element={<SuspenseRoute><Auth /></SuspenseRoute>} />
                <Route path="/auth/reset-password" element={<SuspenseRoute><ResetPassword /></SuspenseRoute>} />
                <Route path="/join" element={<SuspenseRoute><Join /></SuspenseRoute>} />
                <Route path="/unsubscribe" element={<SuspenseRoute><UnsubscribePage /></SuspenseRoute>} />
                <Route path="/onboarding" element={<SuspenseRoute><UserOnboarding /></SuspenseRoute>} />
                <Route path="/getting-started" element={<SuspenseRoute><GettingStarted /></SuspenseRoute>} />

                {/* Protected routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <SuspenseRoute><Index /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/activity/:activityId" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ActivityTimeline /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/account" element={
                  <ProtectedRoute>
                    <SuspenseRoute><AccountPreferences /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin>
                    <SuspenseRoute><AdminDashboard /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/system-templates" element={
                  <ProtectedRoute requireRole="super_admin">
                    <SuspenseRoute><SystemTemplatesAdmin /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/system-tags" element={
                  <ProtectedRoute requireRole="super_admin">
                    <SuspenseRoute><SystemTagsAdmin /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute requireAdmin>
                    <SuspenseRoute><OrganizationSettings /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/organization/integrations" element={
                  <ProtectedRoute requireAdmin>
                    <SuspenseRoute><OrganizationIntegrations /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                {/* Read-only. Publishing stays on /admin/import-review, which
                    is super-admin only — this exists so the team admin who set
                    an integration up can see what their own sync produced. */}
                <Route path="/organization/imports" element={
                  <ProtectedRoute requireAdmin>
                    <SuspenseRoute><TeamImports /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/import-review" element={
                  <ProtectedRoute requireRole="super_admin">
                    <SuspenseRoute><ImportReview /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/admin/weekly-insights" element={
                  <ProtectedRoute requireRole="super_admin">
                    <SuspenseRoute><WeeklyInsightsPage /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/enterprise" element={
                  <ProtectedRoute requireEnterpriseAdmin>
                    <SuspenseRoute><EnterpriseDashboard /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/enterprise/:enterpriseId/organizations" element={
                  <ProtectedRoute requireEnterpriseAdmin>
                    <SuspenseRoute><EnterpriseOrganizations /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/compliance" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ComplianceDashboard /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/manage-activity" element={
                  <ProtectedRoute>
                    <SuspenseRoute><CreateActivity /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/create-template" element={
                  <ProtectedRoute>
                    <SuspenseRoute><CreateTemplate /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/manage-templates" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ManageTemplatesAndActivities /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/actions" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ActionsList /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/risk-issue-logs" element={
                  <ProtectedRoute>
                    <SuspenseRoute><RiskIssueLogs /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/add-risk-issue" element={
                  <ProtectedRoute>
                    <SuspenseRoute><AddRiskIssue /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                {/* RETIRED FROM NAV (ARC-592): Labs iframe — charts now via CPMT + chart packages on GameOps. Keep for direct URL. */}
                <Route path="/strategic-v1" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ArcanixLabs /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/strategic-v2" element={
                  <ProtectedRoute>
                    <SuspenseRoute><StrategicDashboardV2 /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/upcoming-features" element={
                  <ProtectedRoute>
                    <SuspenseRoute><UpcomingFeatures /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/subscription" element={
                  <ProtectedRoute>
                    <SuspenseRoute><Subscription /></SuspenseRoute>
                  </ProtectedRoute>
                } />

                {/* Test routes - Super Admin only */}
                <Route path="/tests" element={
                  <ProtectedRoute>
                    <SuspenseRoute><TestIndex /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/approval_workflow" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ApprovalWorkflowTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/chart-dispatcher" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ChartDispatcherTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/chart-package-test" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ChartPackageTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/revenue-events" element={
                  <ProtectedRoute>
                    <SuspenseRoute><RevenueEventsTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/revenue-events-dispatcher" element={
                  <ProtectedRoute>
                    <SuspenseRoute><RevenueEventsDispatcherTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/chart-events-explorer" element={
                  <ProtectedRoute>
                    <SuspenseRoute><ChartEventsExplorer /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/revenue-attribution" element={
                  <ProtectedRoute>
                    <SuspenseRoute><RevenueAttributionTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/gamescom-revenue-attribution" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamescomRevenueAttributionTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/gamescom-revenue-attribution-with-popup-charts" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamescomRevenueAttributionPopupTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/tests/gamescom-revenue-attribution-with-popup-charts-packages" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamescomRevenueAttributionPopupPackagesTest /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/gamesops-v1" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamesopsV1 /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/gamesops-v2" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamesopsV2 /></SuspenseRoute>
                  </ProtectedRoute>
                } />
                <Route path="/gamesops-v3" element={
                  <ProtectedRoute>
                    <SuspenseRoute><GamesopsV3 /></SuspenseRoute>
                  </ProtectedRoute>
                } />


                <Route path="/api-docs" element={
                  <ProtectedRoute requireAdmin>
                    <SuspenseRoute><ApiDocs /></SuspenseRoute>
                  </ProtectedRoute>
                } />

                <Route path="*" element={<SuspenseRoute><NotFound /></SuspenseRoute>} />
              </Routes>
              <OnboardingSheet />
              <SupportButton />
                  </BrowserRouter>
                </TimezoneProvider>
              </NotificationProvider>
            </TeamProvider>
          </ApprovalModeProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ChunkErrorBoundary>
);

export default App;
